import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronsLeft, ChevronsRight, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/queries/useDashboardData";
import { usePlans } from "@/hooks/queries/usePlans";
import { getUpsell } from "@/lib/upsell";
import { PromoCard } from "@/components/widgets";
import { BrandLockup } from "@/components/brand/Logo";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { isPathActive, primaryNav, secondaryNav, TOUCH_TARGETS, visibleNav, type NavItem } from "./navigation";

interface SidebarProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Render in-flow (inside the mobile sheet) instead of fixed to the viewport. */
  embedded?: boolean;
}

const PROMO_DISMISS_KEY = "sidebar_upsell_dismissed";

/** Logo lockup; kept as BrandMark for existing imports (Login, Landing). */
export function BrandMark({ compact = false, to = "/dashboard" }: { compact?: boolean; to?: string }) {
  return <BrandLockup compact={compact} to={to} />;
}

function NavRow({ item, open, pathname }: { item: NavItem; open: boolean; pathname: string }) {
  const childActive = item.children?.some((c) => isPathActive(pathname, c.href)) ?? false;
  const selfActive = item.href ? isPathActive(pathname, item.href) : false;
  const [expanded, setExpanded] = useState(childActive);

  useEffect(() => {
    if (childActive) setExpanded(true);
  }, [childActive]);

  const rowClass = cn(
    "relative flex w-full items-center gap-3 rounded-xl px-3 h-11 text-[15px] transition-colors",
    selfActive || (childActive && !open) ? "bg-surface-muted text-foreground" : "text-foreground/80 hover:bg-surface-muted/70 hover:text-foreground"
  );
  const indicator = (selfActive || (childActive && !open)) && (
    <span className="absolute -left-3 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-r-full bg-primary" />
  );
  const icon = <item.icon className="h-[19px] w-[19px] flex-shrink-0" strokeWidth={1.6} />;

  if (!open) {
    const target = item.href ?? item.children![0].href;
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <Link to={target} className={cn(rowClass, "justify-center px-0")} aria-label={item.name}>
            {indicator}
            {icon}
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right" className="rounded-lg">
          {item.name}
        </TooltipContent>
      </Tooltip>
    );
  }

  if (item.href) {
    return (
      <Link to={item.href} className={rowClass} aria-current={selfActive ? "page" : undefined}>
        {indicator}
        {icon}
        <span className="truncate">{item.name}</span>
      </Link>
    );
  }

  return (
    <div>
      <button type="button" onClick={() => setExpanded((e) => !e)} className={rowClass} aria-expanded={expanded}>
        {icon}
        <span className="truncate flex-1 text-left">{item.name}</span>
        <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", expanded && "rotate-180")} />
      </button>
      {expanded && (
        <ul className="ml-[21px] mt-1 mb-1 border-l border-border pl-4 flex flex-col gap-0.5">
          {item.children!.map((c) => {
            const active = isPathActive(pathname, c.href);
            return (
              <li key={c.href} className="relative">
                <span className="absolute -left-4 top-1/2 h-px w-3 bg-border" />
                <Link
                  to={c.href}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "flex items-center h-9 px-2 rounded-lg text-sm transition-colors",
                    active ? "bg-surface-muted text-foreground font-medium" : "text-foreground/70 hover:text-foreground"
                  )}
                >
                  {c.name}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export const Sidebar = ({ open, setOpen, embedded = false }: SidebarProps) => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Upsell only for lower self-serve tiers with a *loaded* subscription:
  // never for admins, top tiers, custom plans, or while loading/erroring.
  const { data: subscription } = useSubscription();
  const { data: plans } = usePlans(!isAdmin && !!subscription);
  const upsell = getUpsell({
    tier: subscription?.tier,
    isAdmin,
    plans: plans ?? [],
    tokensUsed: subscription?.tokensUsed,
    tokensLimit: subscription?.tokensLimit,
  });
  // Dismissal is remembered per offer, so upgrading (or nearing the limit) can surface a new one.
  const dismissKey = upsell ? `${PROMO_DISMISS_KEY}:${upsell.target.tier}${upsell.urgent ? ":urgent" : ""}` : "";
  const [dismissedKeys, setDismissedKeys] = useState<string[]>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(PROMO_DISMISS_KEY) || "[]");
      return Array.isArray(saved) ? saved : [];
    } catch {
      return [];
    }
  });
  const showPromo = open && !!upsell && !dismissedKeys.includes(dismissKey);

  return (
    <aside
      className={cn(
        "bg-sidebar text-sidebar-foreground flex flex-col transition-[width] duration-300",
        // the mobile sheet is portaled outside AppLayout, so apply touch sizing here too
        TOUCH_TARGETS,
        embedded ? "h-full w-full" : "fixed inset-y-0 left-0 z-[100]",
        !embedded && (open ? "w-[264px]" : "w-[84px]")
      )}
    >
      <div className={cn("h-[72px] flex items-center flex-shrink-0", open ? "px-6" : "justify-center")}>
        <BrandMark compact={!open} />
      </div>

      <nav className={cn("flex-1 overflow-y-auto overflow-x-hidden pb-4", open ? "px-4" : "px-3")} aria-label="Main">
        <div className="flex flex-col gap-1">
          {visibleNav(primaryNav, isAdmin).map((item) => (
            <NavRow key={item.name} item={item} open={open} pathname={pathname} />
          ))}
        </div>
        <div className="my-4 mx-3 h-px bg-border" />
        <div className="flex flex-col gap-1">
          {secondaryNav.map((item) => (
            <NavRow key={item.name} item={item} open={open} pathname={pathname} />
          ))}
        </div>
      </nav>

      <div className={cn("flex-shrink-0 pb-4", open ? "px-4" : "px-3")}>
        {showPromo && upsell && (
          <div className="mb-3">
            <PromoCard
              icon={<Zap className="h-4 w-4 fill-current" />}
              title={upsell.title}
              body={upsell.body}
              cta={upsell.cta}
              to="/billing"
              onDismiss={() => {
                const next = [...dismissedKeys, dismissKey];
                localStorage.setItem(PROMO_DISMISS_KEY, JSON.stringify(next));
                setDismissedKeys(next);
              }}
            />
          </div>
        )}
        {!embedded && <button
          type="button"
          onClick={() => setOpen(!open)}
          className={cn(
            "flex w-full items-center gap-3 h-11 rounded-xl text-[15px] text-foreground/80 hover:bg-surface-muted/70 hover:text-foreground transition-colors",
            open ? "px-3" : "justify-center"
          )}
          aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
        >
          {open ? <ChevronsLeft className="h-[19px] w-[19px]" strokeWidth={1.6} /> : <ChevronsRight className="h-[19px] w-[19px]" strokeWidth={1.6} />}
          {open && <span>Collapse sidebar</span>}
        </button>}
      </div>
    </aside>
  );
};
